import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Search, Plus, Trash2, Send, FileText, User, Calendar, Mail, Phone, Award, StickyNote, ChevronDown, ChevronUp, Lock, Eye, EyeOff, Sparkles, Undo2, Languages, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { adminApi, getStoredAdminPassword, clearStoredAdminPassword } from "../lib/adminApi";
import { useConfirm } from "../lib/useConfirm";
import AdminLoginForm from "../components/AdminLoginForm";
import ConfirmDialog from "../components/ConfirmDialog";

const LOGO_URL = "/images/ym6gev0b_AE8ECFCA-9BBA-44D9-A534-DED3210EC5A4.png";

export default function PlayerProgress() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!getStoredAdminPassword());
  const { confirmState, requestConfirm, setOpen: setConfirmOpen } = useConfirm();
  
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [campFilter, setCampFilter] = useState("all");           // "all" or camp_id
  const [reportFilter, setReportFilter] = useState("all");       // "all" | "has" | "none"
  const [reportedRegIds, setReportedRegIds] = useState(new Set());
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerNotes, setPlayerNotes] = useState([]);
  const [progressReports, setProgressReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedPlayer, setExpandedPlayer] = useState(null);

  const [noteForm, setNoteForm] = useState({
    note: "",
    category: "general"
  });
  const [showNoteDialog, setShowNoteDialog] = useState(false);

  const [reportForm, setReportForm] = useState({
    overall_assessment: "",
    skills_progress: "",
    areas_for_improvement: "",
    coach_comments: ""
  });
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [isSendingReport, setIsSendingReport] = useState(false);

  // AI Helper: keep the human-typed originals so coach can revert per-section.
  // null = the field has not been AI-rewritten yet; string = the original text.
  const [reportOriginals, setReportOriginals] = useState({
    overall_assessment: null,
    skills_progress: null,
    areas_for_improvement: null,
    coach_comments: null,
  });
  const [isAiRewriting, setIsAiRewriting] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPlayers();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    let result = players;
    if (campFilter !== "all") {
      result = result.filter((p) => p.camp_id === campFilter);
    }
    if (reportFilter === "has") {
      result = result.filter((p) => reportedRegIds.has(p.id));
    } else if (reportFilter === "none") {
      result = result.filter((p) => !reportedRegIds.has(p.id));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((p) =>
        p.participant_name.toLowerCase().includes(q) ||
        (p.camp_name || "").toLowerCase().includes(q) ||
        (p.parent_name || "").toLowerCase().includes(q)
      );
    }
    setFilteredPlayers(result);
  }, [searchQuery, campFilter, reportFilter, reportedRegIds, players]);

  const handleLogout = () => {
    clearStoredAdminPassword();
    setIsAuthenticated(false);
    toast.success("Logged out");
  };

  const fetchPlayers = async () => {
    try {
      const [playersRes, reportsRes] = await Promise.all([
        adminApi.get(`/admin/players`),
        adminApi.get(`/admin/progress-reports`),
      ]);
      setPlayers(playersRes.data);
      setFilteredPlayers(playersRes.data);
      setReportedRegIds(new Set((reportsRes.data || []).map((r) => r.registration_id)));
    } catch (error) {
      console.error("Error fetching players:", error);
      toast.error("Failed to load players");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPlayerDetails = async (registration) => {
    setSelectedPlayer(registration);
    try {
      const [notesRes, reportsRes] = await Promise.all([
        adminApi.get(`/admin/player-notes/${registration.id}`),
        adminApi.get(`/admin/progress-reports/${registration.id}`)
      ]);
      setPlayerNotes(notesRes.data);
      setProgressReports(reportsRes.data);
    } catch (error) {
      console.error("Error fetching player details:", error);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!selectedPlayer) return;

    try {
      await adminApi.post(`/admin/player-notes`, {
        registration_id: selectedPlayer.id,
        participant_name: selectedPlayer.participant_name,
        note: noteForm.note,
        category: noteForm.category
      });
      toast.success("Note added successfully!");
      setShowNoteDialog(false);
      setNoteForm({ note: "", category: "general" });
      fetchPlayerDetails(selectedPlayer);
    } catch (error) {
      console.error("Error adding note:", error);
      toast.error("Failed to add note");
    }
  };

  const handleResendReport = (report) => {
    requestConfirm({
      title: "Resend this progress report?",
      description: `An identical email will be sent to ${report.parent_email}. The report record stays unchanged.`,
      confirmLabel: "Resend Report",
      destructive: false,
      onConfirm: async () => {
        try {
          await adminApi.post(`/admin/progress-reports/${report.id}/resend`);
          toast.success(`Report resent to ${report.parent_email}`);
          fetchPlayerDetails(selectedPlayer);
        } catch (e) {
          toast.error("Failed to resend report");
        }
      },
    });
  };

  const handleDeleteNote = (noteId) => {
    requestConfirm({
      title: "Delete this note?",
      description: "This coach's note will be removed from the player's record.",
      confirmLabel: "Delete Note",
      onConfirm: async () => {
        try {
          await adminApi.delete(`/admin/player-notes/${noteId}`);
          toast.success("Note deleted");
          fetchPlayerDetails(selectedPlayer);
        } catch (error) {
          toast.error("Failed to delete note");
        }
      },
    });
  };

  const resetReportForm = () => {
    setReportForm({
      overall_assessment: "",
      skills_progress: "",
      areas_for_improvement: "",
      coach_comments: "",
    });
    setReportOriginals({
      overall_assessment: null,
      skills_progress: null,
      areas_for_improvement: null,
      coach_comments: null,
    });
  };

  const handleSendReport = async (language = "en", e) => {
    if (e) e.preventDefault();
    if (!selectedPlayer) return;

    setIsSendingReport(true);
    try {
      await adminApi.post(`/admin/progress-reports`, {
        registration_id: selectedPlayer.id,
        language,
        ...reportForm,
      });
      const langLabel = language === "fr" ? "in French" : "in English";
      toast.success(`Progress report sent ${langLabel} to ${selectedPlayer.parent_email}`);
      setShowReportDialog(false);
      resetReportForm();
      fetchPlayerDetails(selectedPlayer);
    } catch (error) {
      console.error("Error sending report:", error);
      toast.error(error?.response?.data?.detail || "Failed to send progress report");
    } finally {
      setIsSendingReport(false);
    }
  };

  const handleAiRewrite = async () => {
    if (!selectedPlayer) return;
    const fields = ["overall_assessment", "skills_progress", "areas_for_improvement", "coach_comments"];
    if (!fields.some((f) => (reportForm[f] || "").trim())) {
      toast.error("Fill in at least one section before using AI Helper");
      return;
    }
    setIsAiRewriting(true);
    try {
      // Snapshot what's currently typed (for revert) — only for fields that
      // had something AND haven't already been AI-rewritten.
      setReportOriginals((prev) => {
        const next = { ...prev };
        fields.forEach((f) => {
          if (next[f] === null && (reportForm[f] || "").trim()) {
            next[f] = reportForm[f];
          }
        });
        return next;
      });
      const { data } = await adminApi.post("/admin/progress-reports/ai-rewrite", {
        participant_name: selectedPlayer.participant_name,
        ...reportForm,
      });
      setReportForm((prev) => ({
        overall_assessment: data.overall_assessment ?? prev.overall_assessment,
        skills_progress: data.skills_progress ?? prev.skills_progress,
        areas_for_improvement: data.areas_for_improvement ?? prev.areas_for_improvement,
        coach_comments: data.coach_comments ?? prev.coach_comments,
      }));
      toast.success("AI rewrite ready — review each section and edit or revert as needed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI rewrite failed");
    } finally {
      setIsAiRewriting(false);
    }
  };

  const revertSection = (field) => {
    const original = reportOriginals[field];
    if (original === null) return;
    setReportForm((prev) => ({ ...prev, [field]: original }));
    setReportOriginals((prev) => ({ ...prev, [field]: null }));
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "skills": return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "attitude": return "bg-green-500/20 text-green-400 border-green-500/30";
      case "improvement": return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      default: return "bg-zinc-500/20 text-zinc-400 border-zinc-500/30";
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <AdminLoginForm
        title="Player Progress"
        subtitle="Coach login required to view player notes and progress reports."
        onSuccess={() => {
          setIsAuthenticated(true);
          fetchPlayers();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-burns-dark">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Burns Elite Baseball" className="h-12 w-auto" />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" className="text-white hover:text-burns-gold">
                  Main Dashboard
                </Button>
              </Link>
              <Button onClick={() => navigate("/admin")} variant="ghost" className="text-white hover:text-burns-gold">
                <ArrowLeft className="w-4 h-4 mr-2" /> Admin Dashboard
              </Button>
              <Button onClick={handleLogout} variant="ghost" className="text-zinc-400 hover:text-white">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Players List */}
          <div className="lg:w-1/3">
            <div className="bg-zinc-950 border border-white/10 rounded-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl text-white">REGISTERED PLAYERS</h2>
                <span className="text-burns-gold text-sm">{filteredPlayers.length} players</span>
              </div>
              
              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  type="text"
                  placeholder="Search players..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-transparent border-white/20 text-white pl-10"
                  data-testid="player-search"
                />
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                <Select value={campFilter} onValueChange={setCampFilter}>
                  <SelectTrigger className="bg-transparent border-white/20 text-white text-sm" data-testid="camp-filter">
                    <SelectValue placeholder="Camp" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/20 text-white">
                    <SelectItem value="all">All camps</SelectItem>
                    {Array.from(new Map(players.map((p) => [p.camp_id, p.camp_name])).entries())
                      .filter(([id]) => id)
                      .map(([id, name]) => (
                        <SelectItem key={id} value={id}>{name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Select value={reportFilter} onValueChange={setReportFilter}>
                  <SelectTrigger className="bg-transparent border-white/20 text-white text-sm" data-testid="report-filter">
                    <SelectValue placeholder="Report status" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/20 text-white">
                    <SelectItem value="all">All players</SelectItem>
                    <SelectItem value="has">Has progress report</SelectItem>
                    <SelectItem value="none">No report yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Player List */}
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {isLoading ? (
                  <p className="text-zinc-400 text-center py-4">Loading players...</p>
                ) : filteredPlayers.length === 0 ? (
                  <p className="text-zinc-400 text-center py-4">No players found</p>
                ) : (
                  filteredPlayers.map(player => (
                    <button
                      key={player.id}
                      onClick={() => fetchPlayerDetails(player)}
                      className={`w-full text-left p-3 rounded-sm border transition-all ${
                        selectedPlayer?.id === player.id 
                          ? 'bg-burns-red/20 border-burns-red/50' 
                          : 'bg-zinc-900 border-white/5 hover:border-white/20'
                      }`}
                      data-testid={`player-card-${player.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white font-medium">{player.participant_name}</p>
                          <p className="text-burns-gold text-xs">{player.camp_name}</p>
                          <p className="text-zinc-500 text-xs mt-1">Age: {player.age} | {player.skill_level}</p>
                        </div>
                        <User className="w-4 h-4 text-zinc-500" />
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Player Details */}
          <div className="lg:w-2/3">
            {selectedPlayer ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                {/* Player Info Card */}
                <div className="bg-zinc-950 border border-white/10 rounded-sm p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="font-heading text-2xl text-white">{selectedPlayer.participant_name}</h2>
                      <p className="text-burns-gold">{selectedPlayer.camp_name}</p>
                    </div>
                    <div className="flex gap-2">
                      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
                        <DialogTrigger asChild>
                          <Button className="btn-secondary text-sm" data-testid="add-note-btn">
                            <StickyNote className="w-4 h-4 mr-2" /> Add Note
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-zinc-900 border-white/20 text-white">
                          <DialogHeader>
                            <DialogTitle className="font-heading text-xl text-burns-gold">ADD NOTE</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleAddNote} className="space-y-4">
                            <div>
                              <Label className="text-zinc-400">Category</Label>
                              <Select value={noteForm.category} onValueChange={(v) => setNoteForm(prev => ({ ...prev, category: v }))}>
                                <SelectTrigger className="bg-transparent border-white/20 text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-white/20">
                                  <SelectItem value="general" className="text-white">General</SelectItem>
                                  <SelectItem value="skills" className="text-white">Skills</SelectItem>
                                  <SelectItem value="attitude" className="text-white">Attitude</SelectItem>
                                  <SelectItem value="improvement" className="text-white">Areas for Improvement</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-zinc-400">Note *</Label>
                              <Textarea
                                value={noteForm.note}
                                onChange={(e) => setNoteForm(prev => ({ ...prev, note: e.target.value }))}
                                className="bg-transparent border-white/20 text-white min-h-[100px]"
                                placeholder="Write your observation..."
                                required
                              />
                            </div>
                            <Button type="submit" className="btn-primary w-full">
                              SAVE NOTE
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>

                      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
                        <DialogTrigger asChild>
                          <Button className="btn-primary text-sm" data-testid="send-report-btn">
                            <Send className="w-4 h-4 mr-2" /> Send Report
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-zinc-900 border-white/20 text-white max-w-lg max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="font-heading text-xl text-burns-gold">SEND PROGRESS REPORT</DialogTitle>
                          </DialogHeader>
                          <p className="text-zinc-400 text-sm">
                            This report will be emailed to <strong className="text-white">{selectedPlayer.parent_email}</strong>
                          </p>
                          {(() => {
                            const lang = (selectedPlayer.language || "en").toLowerCase();
                            const isFr = lang === "fr";
                            return (
                              <div className={
                                "border rounded-md px-3 py-2 text-sm " +
                                (isFr
                                  ? "bg-burns-gold/15 border-burns-gold/40 text-burns-gold"
                                  : "bg-zinc-800/60 border-white/10 text-zinc-300")
                              }>
                                {isFr
                                  ? "🇫🇷 This family registered in French — use Send in French below for an Acadian-French translation."
                                  : "🇨🇦 This family registered in English — use Send in English below."}
                              </div>
                            );
                          })()}
                          <form onSubmit={(e) => handleSendReport("en", e)} className="space-y-4" autoComplete="off">
                            {[
                              { key: "overall_assessment", label: "Overall Assessment", placeholder: "General assessment of the player's performance..." },
                              { key: "skills_progress", label: "Skills Progress", placeholder: "Progress in batting, fielding, throwing, etc..." },
                              { key: "areas_for_improvement", label: "Areas for Improvement", placeholder: "Skills or areas that need more work..." },
                              { key: "coach_comments", label: "Coach's Comments", placeholder: "Personal message to the parent/player..." },
                            ].map(({ key, label, placeholder }) => (
                              <div key={key}>
                                <div className="flex items-center justify-between mb-1">
                                  <Label className="text-zinc-400">{label} *</Label>
                                  {reportOriginals[key] !== null && (
                                    <button
                                      type="button"
                                      onClick={() => revertSection(key)}
                                      className="text-xs text-burns-gold hover:underline flex items-center gap-1"
                                    >
                                      <Undo2 className="w-3 h-3" /> Revert to original
                                    </button>
                                  )}
                                </div>
                                <Textarea
                                  value={reportForm[key]}
                                  onChange={(e) => setReportForm((prev) => ({ ...prev, [key]: e.target.value }))}
                                  className="bg-transparent border-white/20 text-white min-h-[80px]"
                                  placeholder={placeholder}
                                  required
                                />
                              </div>
                            ))}
                            <Button
                              type="button"
                              onClick={handleAiRewrite}
                              disabled={isAiRewriting}
                              className="w-full bg-burns-gold/20 hover:bg-burns-gold/30 border border-burns-gold/40 text-burns-gold"
                              data-testid="ai-rewrite-btn"
                            >
                              {isAiRewriting ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <Sparkles className="w-4 h-4 mr-2" />
                              )}
                              AI Helper — rewrite all sections
                            </Button>
                            <p className="text-zinc-500 text-xs">
                              Uses OpenAI to clean up your draft. Each section gets a "Revert to original" link if you want your text back.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <Button type="submit" className="btn-primary" disabled={isSendingReport}>
                                {isSendingReport ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                                Send in English
                              </Button>
                              <Button
                                type="button"
                                onClick={() => handleSendReport("fr")}
                                disabled={isSendingReport}
                                className="bg-burns-gold hover:bg-burns-gold/80 text-black"
                                data-testid="send-report-fr-btn"
                              >
                                {isSendingReport ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Languages className="w-4 h-4 mr-2" />}
                                Send in French
                              </Button>
                            </div>
                            <p className="text-zinc-500 text-xs">
                              "Send in French" auto-translates the four sections above to Acadian French (via OpenAI) before sending and saving. The original English text is not stored.
                            </p>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-zinc-400">
                      <User className="w-4 h-4 text-burns-gold" />
                      <span>Age: {selectedPlayer.age} | {selectedPlayer.gender}</span>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Award className="w-4 h-4 text-burns-gold" />
                      <span>Level: {selectedPlayer.skill_level}</span>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Mail className="w-4 h-4 text-burns-gold" />
                      <span>{selectedPlayer.parent_email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Phone className="w-4 h-4 text-burns-gold" />
                      <span>{selectedPlayer.parent_phone}</span>
                    </div>
                  </div>
                  {selectedPlayer.medical_info && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-sm">
                      <p className="text-red-400 text-sm"><strong>Medical Info:</strong> {selectedPlayer.medical_info}</p>
                    </div>
                  )}
                </div>

                {/* Notes Section */}
                <div className="bg-zinc-950 border border-white/10 rounded-sm p-6">
                  <h3 className="font-heading text-lg text-white mb-4 flex items-center gap-2">
                    <StickyNote className="w-5 h-5 text-burns-gold" />
                    COACH NOTES ({playerNotes.length})
                  </h3>
                  {playerNotes.length === 0 ? (
                    <p className="text-zinc-500 text-sm">No notes yet. Add your first observation!</p>
                  ) : (
                    <div className="space-y-3">
                      {playerNotes.map(note => (
                        <div key={note.id} className="bg-zinc-900 border border-white/5 rounded-sm p-4">
                          <div className="flex items-start justify-between mb-2">
                            <span className={`text-xs px-2 py-1 rounded border ${getCategoryColor(note.category)}`}>
                              {note.category.charAt(0).toUpperCase() + note.category.slice(1)}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-zinc-500 text-xs">
                                {new Date(note.created_at).toLocaleDateString()}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteNote(note.id)}
                                className="text-red-500 hover:text-red-400 h-6 w-6 p-0"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-zinc-300 text-sm">{note.note}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Progress Reports History */}
                <div className="bg-zinc-950 border border-white/10 rounded-sm p-6">
                  <h3 className="font-heading text-lg text-white mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-burns-gold" />
                    SENT REPORTS ({progressReports.length})
                  </h3>
                  {progressReports.length === 0 ? (
                    <p className="text-zinc-500 text-sm">No progress reports sent yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {progressReports.map(report => (
                        <div key={report.id} className="bg-zinc-900 border border-white/5 rounded-sm">
                          <div className="flex items-center justify-between gap-2 p-2 pr-3">
                            <button
                              onClick={() => setExpandedPlayer(expandedPlayer === report.id ? null : report.id)}
                              className="flex-1 p-2 text-left"
                            >
                              <p className="text-white font-medium">
                                {new Date(report.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                {report.language === "fr" && <span className="ml-2 text-burns-gold text-xs uppercase">FR</span>}
                              </p>
                              <p className="text-burns-gold text-xs">{report.camp_name || "—"}</p>
                              <p className="text-zinc-500 text-xs">
                                To: {report.parent_email}
                                {report.last_sent_at && (
                                  <span className="ml-2 text-burns-gold">
                                    · resent {new Date(report.last_sent_at).toLocaleDateString()} ({report.resend_count}×)
                                  </span>
                                )}
                              </p>
                            </button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handleResendReport(report); }}
                              className="text-burns-gold hover:bg-burns-gold/10"
                              data-testid={`resend-report-${report.id}`}
                            >
                              <Send className="w-4 h-4 mr-1" /> Resend
                            </Button>
                            <button
                              onClick={() => setExpandedPlayer(expandedPlayer === report.id ? null : report.id)}
                              className="text-zinc-400 p-1"
                              aria-label="Toggle report details"
                            >
                              {expandedPlayer === report.id ? (
                                <ChevronUp className="w-5 h-5" />
                              ) : (
                                <ChevronDown className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                          {expandedPlayer === report.id && (
                            <div className="px-4 pb-4 space-y-3 border-t border-white/5 pt-3">
                              <div>
                                <p className="text-burns-gold text-xs mb-1">Overall Assessment</p>
                                <p className="text-zinc-300 text-sm">{report.overall_assessment}</p>
                              </div>
                              <div>
                                <p className="text-burns-gold text-xs mb-1">Skills Progress</p>
                                <p className="text-zinc-300 text-sm">{report.skills_progress}</p>
                              </div>
                              <div>
                                <p className="text-burns-gold text-xs mb-1">Areas for Improvement</p>
                                <p className="text-zinc-300 text-sm">{report.areas_for_improvement}</p>
                              </div>
                              <div>
                                <p className="text-burns-gold text-xs mb-1">Coach's Comments</p>
                                <p className="text-zinc-300 text-sm">{report.coach_comments}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ) : (
              <div className="bg-zinc-950 border border-white/10 rounded-sm p-12 text-center">
                <User className="w-16 h-16 text-burns-gold mx-auto mb-4 opacity-50" />
                <h3 className="text-white font-heading text-xl mb-2">SELECT A PLAYER</h3>
                <p className="text-zinc-500">Choose a player from the list to view their progress and add notes.</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <ConfirmDialog
        open={confirmState.open}
        onOpenChange={setConfirmOpen}
        title={confirmState.title}
        description={confirmState.description}
        confirmLabel={confirmState.confirmLabel}
        cancelLabel={confirmState.cancelLabel}
        destructive={confirmState.destructive}
        onConfirm={confirmState.onConfirm}
      />
    </div>
  );
}
